/* eslint-disable functional/no-expression-statement */
import * as D3Scale from "d3-scale";
import * as D3Selection from "d3-selection";
import { Option, pipe } from "fp";
import { Draft as Mutable } from "immer";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

import * as Animation from "~/Animation";
import { Asset } from "~/Asset";
import * as Color from "~/Color";
import { Company } from "~/Company";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";
import * as Typography from "~/Typography";

import * as Axis from "./Axis";
import * as DropShadow from "./DropShadow";
import * as Milestone from "./Milestone";
import * as PairingEvents from "./PairingEvents";
import * as EventPopover from "./Popover";
import * as RevisionEvents from "./RevisionEvents";
import { SelectorChart } from "./Selector";
import * as Selector from "./Selector";
import * as StatusEvents from "./StatusEvents";
import * as ViolationEvents from "./ViolationEvents";

export * as Icon from "./Icon";
export * as Milestones from "./Milestone";
export * as Selector from "./Selector";
export * as StatusEvents from "./StatusEvents";

export type Props = {
  readonly height: number;
  readonly width: number;
};

export type Status = "OFF" | "SB" | "D" | "ON";

export type LogChartData = {
  readonly status: Driver.Status.Status;
  readonly start: number;
  readonly stop: number;
  readonly source: Log.Event.Source.Type.Type;
  readonly event: Log.Event.Event;
};

export type PairingChartData = {
  readonly start: number;
  readonly assetName: string;
};

export type SVG = D3Selection.Selection<
  SVGGElement,
  unknown,
  HTMLElement,
  LogChartData
>;

export type YScale = (x: string) => number | undefined;

export type ChartGeneratorProps = {
  readonly svg: SVG;
  readonly statusEvents: readonly LogChartData[];
  readonly mergedStatusEvents: readonly LogChartData[];
  readonly yScale: YScale;
  readonly yScaleRight: YScale;
  readonly yBand: readonly Status[];
  readonly xScale: D3Scale.ScaleTime<number, number, never>;
  readonly width: number;
  readonly height: number;
  readonly chartWidth: number;
  readonly chartHeight: number;
  readonly topMargin: number;
  readonly bottomMargin: number;
  readonly leftMargin: number;
  readonly pathMarginTop: number;
  readonly chartTimeStart: Time.DateTime;
  readonly chartTimeEnd: Time.DateTime;
  readonly revisionEvents: readonly LogChartData[];
  readonly pairingEvents: readonly PairingChartData[];
  readonly violationEvents: readonly Log.Violation.Violation[];
  readonly dayStart: Time.DateTime;
  readonly driverTimezone: Time.Zone;
  readonly defaultTimezone: Time.Zone;
  readonly driversReportingLocation?: Company.Location;
};

const yBand: readonly Status[] = ["ON", "D", "SB", "OFF"];

const generateChart = (
  className: string,
  height: number,
  width: number,
  statusEvents: readonly LogChartData[],
  mergedStatusEvents: readonly LogChartData[],
  revisionEvents: readonly LogChartData[],
  pairingEvents: readonly PairingChartData[],
  violationEvents: readonly Log.Violation.Violation[],
  dayStart: Time.DateTime,
  driverTimezone: Time.Zone,
  defaultTimezone: Time.Zone,
  driversReportingLocation?: Company.Location
): ChartGeneratorProps | undefined => {
  if (!statusEvents || statusEvents.length < 1) {
    return;
  }
  const leftMargin = 80;
  const rightMargin = 80;
  const specialEventsMarginHeight = 20;

  // eslint-disable-next-line functional/no-let
  let topMargin = 20;
  // eslint-disable-next-line functional/no-let
  let bottomMargin = 45;
  // eslint-disable-next-line functional/no-let
  let pathMarginTop = 27;

  if (pairingEvents.length > 0) {
    topMargin += specialEventsMarginHeight;
    bottomMargin += specialEventsMarginHeight;
    pathMarginTop += 6;
  } else {
    topMargin += 23;
  }

  const chartHeight: number = height - topMargin - bottomMargin;
  const chartWidth: number = width - leftMargin - rightMargin;

  const { start: chartTimeStart, stop: chartTimeEnd } = pipe(
    Time.DateTime.toDayInterval(dayStart, driverTimezone),
    Time.Interval.Stopped.expandLeft(Time.Duration.hours(12)),
    Time.Interval.Stopped.expandRight(Time.Duration.hours(12))
  ).interval;

  D3Selection.selectAll(`.${className}-svg`).remove();

  const yScale = D3Scale.scaleBand().domain(yBand).range([chartHeight, 0]);
  const yScaleRight = D3Scale.scaleBand().domain(yBand).range([chartHeight, 0]);

  const xScale = D3Scale.scaleTime()
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    .domain([chartTimeStart.dateTime, chartTimeEnd.dateTime])
    .range([0, chartWidth])
    .clamp(true);

  const svg = D3Selection.select(`.${className}`)
    .append("svg")
    .attr("class", `${className}-svg`)
    .attr("width", width)
    .attr("height", height)
    .attr("style", `background-color: ${Color.white()}`)
    .append("g")
    .attr("transform", `translate(${leftMargin},${topMargin})`);

  const generationProps: ChartGeneratorProps = {
    svg,
    statusEvents,
    mergedStatusEvents,
    yScale,
    yScaleRight,
    yBand,
    xScale,
    width,
    height,
    chartWidth,
    chartHeight,
    topMargin,
    bottomMargin,
    leftMargin,
    pathMarginTop,
    chartTimeStart,
    chartTimeEnd,
    revisionEvents,
    pairingEvents,
    violationEvents,
    dayStart,
    driverTimezone,
    defaultTimezone,
    driversReportingLocation,
  };

  DropShadow.generate(generationProps);

  Axis.Xaxis.generate(generationProps);
  if (pairingEvents.length > 0) {
    PairingEvents.generate(generationProps);
  }
  Axis.Yaxis.Left.generate(generationProps);
  Axis.Yaxis.Left.Icons.generate(generationProps);
  Axis.Yaxis.Right.generate(generationProps);

  if (revisionEvents.length > 0) {
    RevisionEvents.generate(generationProps);
  }
  StatusEvents.generate(generationProps);
  Milestone.generate(generationProps);

  if (violationEvents.length > 0) {
    ViolationEvents.generate(generationProps);
  }

  return generationProps;
};

const StyleContainer = styled.div`
  display: flex;
  flex: 1;
  height: 25em;
  & .axis-x {
    & .domain {
      stroke: ${Color.veryWhiteGrey()};
    }
    & .tick > line {
      stroke: ${Color.veryWhiteGrey()};
    }
  }

  & .watermark-time-violation {
    color: ${Color.guardsmanRed()};
  }
`;

export const Component: Component<{
  readonly Action?: Component;
  readonly route: Log.Route;
}> = ({ Action, route }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [forceRender, toggleForceRender] = useState(false);
  const [timeout, updateTimeout] = useState<number | undefined>(undefined);
  const [brushHandlers, setBrushHandlers] = useState(
    SelectorChart.BrushEventHandlers.defaultBrushHandlers
  );
  const driversReportingLocation = Driver.useFromReference({
    ...Option.toUndefined(Driver.useReferenceFromRoute()),
    found: (driver) =>
      driver?.homeOffice && driver.homeOffice?.location
        ? driver.homeOffice
        : undefined,
    missing: () => undefined,
  });

  const logDate = { dateTime: route.dateTime };

  const defaultZone = Time.Zone.use();
  const driverZone = Driver.useFromReference({
    ...Option.toUndefined(Driver.useReferenceFromRoute()),
    found: (driver) => driver?.time ?? defaultZone,
    missing: () => defaultZone,
  });

  const statusEvents = Log.Data.useAllEventsSorted(route);
  const mergedStatusEvents = Log.Data.mergeAdjacentLikeEventsSpanningMidnight(
    statusEvents,
    driverZone
  );
  const statusEventsSignature = Timeline.Section.eventsSignature(
    Log.id(route),
    statusEvents
  );

  const chartData: readonly LogChartData[] = StatusEvents.mapData(
    statusEvents,
    logDate.dateTime,
    driverZone
  );

  const mergedChartData: readonly LogChartData[] = StatusEvents.mapData(
    mergedStatusEvents,
    logDate.dateTime,
    driverZone
  );

  const revisionEvents = chartData;

  const violationEvents = Log.Timeline.Violations.useEvents(route) as Mutable<
    readonly Log.Violation.Violation[]
  >;
  const violationEventsSignature = Timeline.Section.eventsSignature(
    Log.id(route),
    violationEvents
  );

  const pairingEvents: readonly PairingChartData[] = pipe(
    Log.Timeline.PairingStatus.useEvents(route),
    (pairings) =>
      pairings.map((pairing) => ({
        start: pairing.interval.start.dateTime,
        assetName: Asset.name(pairing),
      }))
  );
  const pairingEventsSignature = pipe(
    Log.Timeline.PairingStatus.useEvents(route),
    (pairings) => Timeline.Section.eventsSignature(Log.id(route), pairings)
  );

  const {
    current: lastBrushSelection,
    updateState: saveBrushSelection,
  } = Data.SharedState.use<Timeline.Brush.BrushSelection>(
    "OverviewBrushSelection",
    [0, 0]
  );

  useEffect(() => {
    const resizeHandler = () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
      const id = window.setTimeout(() => toggleForceRender(!forceRender), 800);
      updateTimeout(id);
    };
    window.addEventListener("resize", resizeHandler);

    return () => window.removeEventListener("resize", resizeHandler);
  });

  useLayoutEffect(() => {
    if (containerRef && containerRef?.current && chartData.length > 0) {
      const containerHeight = containerRef?.current.offsetHeight;
      const containerWidth = containerRef?.current.offsetWidth;

      const chartProps = generateChart(
        "log-chart",
        containerHeight,
        containerWidth,
        chartData,
        mergedChartData,
        revisionEvents,
        pairingEvents,
        violationEvents,
        logDate,
        driverZone,
        defaultZone,
        driversReportingLocation
      );

      if (chartProps && chartProps.svg) {
        const handlers = Selector.SelectorChart.generateSelectorChart({
          chartProps,
          data: chartData,
          width: containerWidth,
          defaultZone,
          driverZone,
          saveBrushSelection,
        });

        if (
          lastBrushSelection &&
          (lastBrushSelection[0] !== 0 || lastBrushSelection[1] !== 0)
        ) {
          handlers.onSetBrush(lastBrushSelection);
        }
        setBrushHandlers(handlers);
      }
    }
  }, [
    statusEventsSignature,
    pairingEventsSignature,
    violationEventsSignature,
    forceRender,
    defaultZone,
  ]);

  if (chartData.length < 1) {
    return null;
  }

  return (
    <Layout.Section.Container.Component>
      <Header.Component title="Overview" Left={Action} events={brushHandlers} />
      <Layout.Section.Container.Component bottom>
        <Animation.FadeIn.Component
          style={{ minHeight: "25em" }}
          ref={containerRef}
        >
          <StyleContainer className="log-chart" />
          <EventPopover.Component />
        </Animation.FadeIn.Component>
        <Selector.Component />
      </Layout.Section.Container.Component>
    </Layout.Section.Container.Component>
  );
};

export namespace Header {
  export type HeaderProps = {
    readonly title: string;
    readonly Left?: Component;
    readonly events: Timeline.Scroll.Events;
  };
  export const Component: Component<HeaderProps> = ({
    title,

    Left,
    events,
  }) => {
    return (
      <Layout.Section.Header.Component
        Left={Left}
        Right={() => <Timeline.Controller {...events} />}
      >
        <Title title={title} />
      </Layout.Section.Header.Component>
    );
  };

  export const Title: Component<{ readonly title: string }> = ({ title }) => (
    <Layout.Flex.Component justify="center">
      <Typography.H2.Component>{title}</Typography.H2.Component>
    </Layout.Flex.Component>
  );
}
